import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import { Descriptions, Input, Spin, Timeline, Tooltip } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  createPriceSheetAPI,
  duplicatePriceSheetAPI,
  getPriceSheetItemsListAPI,
  getPriceSheetVariablesAPI,
  updatePriceSheetAPI,
} from "./priceSheetApis";
import { BtnLoader } from "../../../Components/Components";
import { svg } from "../../../Assets/svg";
import FullScreenLoader from "../../Components/FullScreenLoader";
import PricesheetNoteModal from "./Components/PricesheetNoteModal";
import TextArea from "antd/es/input/TextArea";
import { GetPricesheetHistoryAPI } from "../../Redux/teamMember";
import { momentFullTime } from "../../../helpers/utils";
import { Link } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { InfoCircleOutlined } from "@ant-design/icons";
import { GetPricesheetVariablesInfoAPI } from "../../Redux/staticData";
import ViewPricesheetVariableModal from "./Components/VaribaleInformationDetailsModal";

enum Modes {
  CREATE = "create",
  DUPLICATE = "duplicate",
  EDIT = "edit",
}

const CreatePriceSheet = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditData, setIsEditData] = useState<any>(null);

  const [variableInformationData, setVariableInformationData] =
    useState<any>(null);
  const [variableInformationModal, setVariableInformationModal] =
    useState<any>(false);
  const { pricesheetHistory } = useAppSelector(
    (state: RootState) => state.teamMember,
  );
  const { pricesheetVariablesInfo } = useAppSelector(
    (state: RootState) => state?.staticData,
  );

  useEffect(() => {
    dispatch(GetPricesheetVariablesInfoAPI());
  }, [dispatch]);

  // const [openNoteModal, setOpenNoteModal] = useState(false);
  // const [submitedValues, setSubmitedValues] = useState<any>(null);

  const getPageTitle = () => {
    switch (location.pathname.split("/").pop()) {
      case "edit":
        return "Modify Price Sheet";
      case "duplicate":
        return "Duplicate Price Sheet";
      case "create":
      default:
        return "Create Price Sheet";
    }
  };

  const getPageMode = () => {
    switch (location.pathname.split("/").pop()) {
      case "edit":
        return Modes.EDIT;
      case "duplicate":
        return Modes.DUPLICATE;
      case "create":
      default:
        return Modes.CREATE;
    }
  };

  const initialValues = {
    name: "",
    version: "",
    description: "",
    uprightSteel: 800,
    bracingSteel: 800,
    beamSteel: 800,
    anchorPlateSteel: 1200,
    recoveryRate: 45,
    paintPrice: 3.5,
    scrapRate: 2,
    boltPrice: 9.6,
    boltPriceM12: 21.3,
    boltPriceM10_90: 11.18,
    boltPriceM10_20: 5.62,
    boltPriceM8_35: 4.08,
    boltPerFootplatePrice: 0.213,
    anchorPrice: 1.44,
    spacerPrice: 27,
    profileRate82: 245,
    profileRate100: 225,
    profileRate120: 225,
    bracingRate: 200,
    meLineRate: 175,
    cbLineRate: 275,
    obLineRate: 375,
    hotDipGalvanising: 735,
    lsUprightRate: 175,
    sheetSteelPrice: 1000,
    driveInRailsPrice: 175,
    driveInRailsSteelPrice: 878,
    boltPriceM8_16: 5.62,
    boltPriceM8_50_thunder_bolts: 4.08,
    shelving_galv_plate_price: 842,
    shelving_cr_plate_price: 825,
    boltless_upright_steel_price: 175,
    endframe_bipoles_height_price: 10,
    finishSurcharge:12,
    monopoles_uprights_price: 6,

    // Component Targets
    rate_me156_small: 41,
    rate_me156_large: 43,
    rate_me120_small: 41,
    rate_me120_large: 43,
    rate_me110_small: 46,
    rate_me110_large: 43,
    rate_cb95_small: 46,
    rate_cb95_large: 43,
    rate_ob101_small: 46,
    rate_ob101_large: 49,
    rate_pallet_support: 65,
    rate_beams_upto_1_2m: 140,
    rate_beams_upto_2_7m: 120,
    rate_beams_2_75m_to_3_3m: 100,
    rate_beams_3_35m_plus: 80,

    rate_cut_backstops: 50,
    rate_drill_backstops: 60,
    rate_drill_drive_beams: 20,

    cd146_angle_1m_to_3m: 22,
    cd146_angle_3m_to_4m: 17,
    cd123_angle_1m_to_3m: 24,
    cd123_angle_3m_to_4m: 19,
    cd95_angle_1m_to_3m: 24,
    cd95_angle_3m_to_4m: 19,
    cd86_angle_1m_to_3m: 25,
    cd86_angle_3m_to_4m: 20,

    // Shelving Production Rates
    punch_shelves_300_to_450: 60,
    punch_shelves_500_pluse: 45,
    folding_shelves: 55,
    punch_nb_crossties: 180,
    fold_nb_crossties: 150,
    punch_nb_feet: 400,
    fold_nb_feet: 300,
    punch_nb_backs: 24,
    punch_adjustable_dividers: 100,
    punch_fixed_dividers: 100,
    fold_adjustable_dividers: 120,
    fold_fixed_dividers: 60,
  };

  const validationSchema = Yup.object().shape(
    Object.keys(initialValues)
      .filter((key) => key !== "version" && key !== "description")
      .reduce((acc: any, key) => {
        if (key === "name") {
          acc[key] = Yup.string().trim().required("Name is required");
        } else {
          acc[key] = Yup.number().required("This field is required");
        }
        return acc;
      }, {}),
  );

  const hasChangesInValues = (payload: any, oldData: any) => {
    for (const key in payload) {
      // skip ignored fields
      if (key === "name" || key === "version" || key == "description") continue;
      const newValue = payload[key];
      const oldValueObj = oldData[key];
      // if key doesn't exist in oldData
      if (!oldValueObj) return true;

      const oldValue = oldValueObj.value;

      if (newValue !== oldValue) {
        return true;
      }
    }

    return false;
  };

  const targetDescriptionRef = useRef<HTMLTextAreaElement>(null);
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      setSaving(true);
      const numericValues = Object.keys(values)
        .filter(
          (key) => key !== "name" && key !== "version" && key !== "description",
        )
        .reduce((acc: any, key) => {
          const val = values[key as keyof typeof values];
          acc[key] = val === 0 ? 0 : Number(val);
          return acc;
        }, {});

      const payload: any = {
        ...(id &&
          (getPageMode() === Modes.EDIT
            ? { id: id }
            : { duplicate_from_price_id: id })),
        name: values?.name,
        version: values?.version,
        description: values?.description?.trim(),
        ...numericValues,
      };
      const isEditMode = getPageMode() === Modes.EDIT;
      let isChangeNameOrVersion = false;
      if (isEditMode) {
        const findIsChange = hasChangesInValues(
          { ...numericValues },
          isEditData,
        );
        payload.recalculate = findIsChange;
        if (
          values?.name?.trim() !== isEditData?.name?.trim() ||
          values?.version?.trim() !== isEditData?.version?.trim()
        ) {
          isChangeNameOrVersion = true;
        }
      }
      if (!values?.description?.trim()) {
        if (!isEditMode || payload?.recalculate || isChangeNameOrVersion) {
          formik?.setErrors({ description: "Notes is required" });
          targetDescriptionRef.current?.focus();
          setSaving(false);
          return;
        }
      }
      const api =
        getPageMode() === Modes.DUPLICATE
          ? duplicatePriceSheetAPI
          : getPageMode() === Modes.EDIT
          ? updatePriceSheetAPI
          : createPriceSheetAPI;

      dispatch(
        api({
          payload,
          onSuccess: () => {
            setSaving(false);
            navigate("/price-sheet");
          },
          onError: () => {
            setSaving(false);
          },
        }),
      );
    },
  });

  const fetchVariables = () => {
    setLoading(true);
    dispatch(
      getPriceSheetVariablesAPI({
        onSuccess: (data: any) => {
          console.log("API: ", data);
          const priceSheetData = data?.variables;
          if (priceSheetData) {
            const newValues = { ...initialValues };
            Object.keys(initialValues).forEach((key) => {
              if (
                priceSheetData[key] &&
                typeof priceSheetData[key] === "object"
              ) {
                (newValues as any)[key] = priceSheetData[key].value ?? "";
              }
            });
            if (getPageMode() === Modes.DUPLICATE) {
              newValues.name = priceSheetData.name + " (Copy)";
            } else {
              newValues.name = priceSheetData.name;
            }
            newValues.version = priceSheetData.version;
            formik.setValues(newValues);
          }
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      }),
    );
  };

  useEffect(() => {
    if (getPageMode() === Modes.CREATE) {
      fetchVariables();
    }
  }, []);

  const fetchDetails = () => {
    if (!id) return;
    setLoading(true);
    dispatch(
      getPriceSheetItemsListAPI({
        price_id: id as string,
        filters: { page: 1, limit: 1 },
        onSuccess: (data: any) => {
          const priceSheetData = data?.priceSheetData;
          if (priceSheetData) {
            const newValues = { ...initialValues };
            Object.keys(initialValues).forEach((key) => {
              if (
                priceSheetData[key] &&
                typeof priceSheetData[key] === "object"
              ) {
                (newValues as any)[key] = priceSheetData[key].value ?? "";
              }
            });
            if (getPageMode() === Modes.DUPLICATE) {
              newValues.name = priceSheetData.name + " (Copy)";
            } else {
              newValues.name = priceSheetData.name;
            }
            newValues.version = priceSheetData.version;
            setIsEditData(priceSheetData);
            formik.setValues(newValues);
          }
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      }),
    );
  };

  useEffect(() => {
    fetchDetails();
    if (id) {
      dispatch(
        GetPricesheetHistoryAPI({ pricesheetId: id, page: 1, limit: 3 }, false),
      );
    }
  }, [id]);

  const variableCosts = [
    {
      label: "Uprights Steel Price",
      name: "uprightSteel",
      unit: "£/Ton",
    },
    {
      label: "Bracing Steel Price",
      name: "bracingSteel",
      unit: "£/Ton",
    },
    { label: "Beam Steel Price", name: "beamSteel", unit: "£/Ton" },
    {
      label: "Anchor Plate Steel Price",
      name: "anchorPlateSteel",
      unit: "£/Ton",
    },
    { label: "Overhead Rate", name: "recoveryRate", unit: "£/hr" },
    { label: "Powder Coating Price", name: "paintPrice", unit: "£/Kg" },
    { label: "Scrap %", name: "scrapRate", unit: "%" },
    {
      label: "M10x65 Bolts&Nuts",
      name: "boltPrice",
      unit: "£/100",
    },
    {
      label: "M12x80 Bolts&Nuts",
      name: "boltPriceM12",
      unit: "£/100",
    },
    {
      label: "M10x90 Bolts&Nuts",
      name: "boltPriceM10_90",
      unit: "£/100",
    },
    {
      label: "M10x20 Bolts&Nuts",
      name: "boltPriceM10_20",
      unit: "£/100",
    },
    {
      label: "M8x35 Bolts&Nuts",
      name: "boltPriceM8_35",
      unit: "£/100",
    },
    {
      label: "M8x16 Bolts&Nuts",
      name: "boltPriceM8_16",
      unit: "£/100",
    },
    {
      label: "M8x50 Thunder Bolts",
      name: "boltPriceM8_50_thunder_bolts",
      unit: "£/100",
    },
    { label: "Anchor Plates", name: "anchorPrice", unit: "£/each" },
    { label: "Spacers", name: "spacerPrice", unit: "£/1000" },
    {
      label: "82 Uprights m per hour",
      name: "profileRate82",
      unit: "m/hr",
    },
    {
      label: "100 Uprights m per hour",
      name: "profileRate100",
      unit: "m/hr",
    },
    {
      label: "120 Uprights m per hour",
      name: "profileRate120",
      unit: "m/hr",
    },
    {
      label: "Bracing m per hour",
      name: "bracingRate",
      unit: "m/hr",
    },
    {
      label: "ME Line m per hour",
      name: "meLineRate",
      unit: "m/hr",
    },
    {
      label: "CB Line m per hour",
      name: "cbLineRate",
      unit: "m/hr",
    },
    {
      label: "OB Line m per hour",
      name: "obLineRate",
      unit: "m/hr",
    },
    {
      label: "Hot Dip Galvanising",
      name: "hotDipGalvanising",
      unit: "£/per Ton",
    },
    {
      label: "LS Upright m per hour",
      name: "lsUprightRate",
      unit: "m/hr",
    },
    {
      label: "Sheet Steel Price",
      name: "sheetSteelPrice",
      unit: "£/per Ton",
    },
    {
      label: "Drive In Rail m per hour",
      name: "driveInRailsPrice",
      unit: "m/hr",
    },
    {
      label: "Drive In Rail Steel Price",
      name: "driveInRailsSteelPrice",
      unit: "£/per Ton",
    },
    {
      label: "Shelving Galv Plate Price",
      name: "shelving_galv_plate_price",
      unit: "£/per Ton",
    },
    {
      label: "Shelving CR Plate Price",
      name: "shelving_cr_plate_price",
      unit: "£/per Ton",
    },
    {
      label: "Endframe & Bipoles Additional Cost",
      name: "endframe_bipoles_height_price",
      unit: "£/per m",
    },
    {
      label: "Monopoles & Uprights Additional Cost",
      name: "monopoles_uprights_price",
      unit: "£/per m",
    },
    {
      label: "Hot Dip Finish Surcharge",
      name: "finishSurcharge",
      unit: "%",
    },
  ];

  const componentProducts = [
    {
      label: "ME156 / CB146 / ME139 (1m - 2.7m) 1 Person",
      name: "rate_me156_small",
    },
    {
      label: "ME156 / CB146 / ME139 (2.7m - 4m) 2 People",
      name: "rate_me156_large",
    },
    {
      label: "ME120 / CB123 (1m - 2.7m) 1 Person",
      name: "rate_me120_small",
    },
    {
      label: "ME120 / CB123 (2.7m - 4m) 2 People",
      name: "rate_me120_large",
    },
    {
      label: "ME110 / CB103 / ME99 (1m - 2.7m) 1 Person",
      name: "rate_me110_small",
    },
    {
      label: "ME110 / CB103 / ME99 (2.7m - 4m) 2 People",
      name: "rate_me110_large",
    },
    {
      label: "CB95 / ME90 / CB86 (1m - 2.7m) 1 Person",
      name: "rate_cb95_small",
    },
    {
      label: "CB95 / ME90 / CB86 (2.7m - 4m) 2 People",
      name: "rate_cb95_large",
    },
    {
      label: "OB101 / OB87 / OB60 (1m - 2.7m)",
      name: "rate_ob101_small",
    },
    {
      label: "OB101 / OB87 / OB60 (2.7m - 4m)",
      name: "rate_ob101_large",
    },
    {
      label: "Pallet Support / Chipboard Support (1 Person)",
      name: "rate_pallet_support",
    },
    {
      label: "Beams Upto 1.2m",
      name: "rate_beams_upto_1_2m",
    },
    {
      label: "Beams Upto 2.7m",
      name: "rate_beams_upto_2_7m",
    },
    {
      label: "Beams 2.75m - 3.3m",
      name: "rate_beams_2_75m_to_3_3m",
    },
    {
      label: "Beams 3.35m +",
      name: "rate_beams_3_35m_plus",
    },
    {
      label: "Cut Backstops",
      name: "rate_cut_backstops",
    },
    {
      label: "Drill Backstops",
      name: "rate_drill_backstops",
    },
    {
      label: "Drill Drive Beams",
      name: "rate_drill_drive_beams",
    },
    {
      label: "CD146 Angle 1m - 3m",
      name: "cd146_angle_1m_to_3m",
    },
    {
      label: "CD146 Angle 3m - 4m",
      name: "cd146_angle_3m_to_4m",
    },
    {
      label: "CD123 Angle 1m - 3m",
      name: "cd123_angle_1m_to_3m",
    },
    {
      label: "CD123 Angle 3m - 4m",
      name: "cd123_angle_3m_to_4m",
    },
    {
      label: "CD95 Angle 1m - 3m",
      name: "cd95_angle_1m_to_3m",
    },
    {
      label: "CD95 Angle 3m - 4m",
      name: "cd95_angle_3m_to_4m",
    },
    {
      label: "CD86 Angle 1m - 3m",
      name: "cd86_angle_1m_to_3m",
    },
    {
      label: "CD86 Angle 3m - 4m",
      name: "cd86_angle_3m_to_4m",
    },
    {
      label: "Punch Shelves 300-450mm",
      name: "punch_shelves_300_to_450",
    },
    {
      label: "Punch Shelves 500mm+",
      name: "punch_shelves_500_pluse",
    },
    {
      label: "Folding Shelves",
      name: "folding_shelves",
    },
    {
      label: "Punch NB Crossties",
      name: "punch_nb_crossties",
    },
    {
      label: "Fold NB Crossties",
      name: "fold_nb_crossties",
    },
    {
      label: "Punch NB Feet",
      name: "punch_nb_feet",
    },
    {
      label: "Fold NB Feet",
      name: "fold_nb_feet",
    },
    {
      label: "Punch NB Backs",
      name: "punch_nb_backs",
    },
    {
      label: "Punch Adjustable Dividers",
      name: "punch_adjustable_dividers",
    },
    {
      label: "Punch Fixed Dividers",
      name: "punch_fixed_dividers",
    },
    {
      label: "Fold Adjustable Dividers",
      name: "fold_adjustable_dividers",
    },
    {
      label: "Fold Fixed Dividers",
      name: "fold_fixed_dividers",
    },
    {
      label: "Boltless Upright m per hour",
      name: "boltless_upright_steel_price",
    },
  ];

  return (
    <div className="page-content h-100 px-40 py-40 bg-light d-flex flex-column overflow-y-auto">
      <div className="mb-3 d-flex flex-column gap-md-2 gap-0">
        <div className="d-flex flex-md-nowrap flex-wrap gap-sm-3 justify-content-between align-items-center">
          <h2 className="text-black fw-bolder page-title mb-0 d-flex align-items-center gap-2">
            <span
              className="cursor-pointer svg-24"
              onClick={() => navigate(-1)}
            >
              {svg?.backArrow || "←"}
            </span>
            {getPageTitle()}
          </h2>
          <div className="d-md-flex d-none justify-content-end align-items-center gap-3">
            <button
              className="btn btn-lg btn-outline-secondary mob-btn text-uppercase"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              className="btn btn-lg btn-primary mob-btn text-uppercase"
              onClick={() => formik.handleSubmit()}
            >
              {saving ? (
                <BtnLoader />
              ) : getPageMode() === Modes.CREATE ? (
                "Create"
              ) : getPageMode() === Modes.DUPLICATE ? (
                "Duplicate"
              ) : (
                "Update"
              )}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <FullScreenLoader />
      ) : (
        <>
          <h3 className="fs-4 fw-bold text-black mb-md-3 mb-2">
            Basic Details
          </h3>
          <div className="card border-0 rounded-4 mb-md-4 mb-3 shadow-sm">
            <div className="card-body p-md-4 p-3">
              <div className="row g-md-4 g-3">
                <div className="col-lg-6 col-md-6" key="name">
                  <div className="form-item">
                    <label className="custom-label mb-1 ff">Name</label>
                    <Input
                      name="name"
                      className="form-control form-control-lg"
                      value={formik.values.name}
                      placeholder="Name"
                      size="large"
                      status={
                        formik.touched.name && formik.errors.name ? "error" : ""
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        formik.setFieldValue("name", val);
                      }}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.name && formik.errors.name && (
                      <div className="text-danger mt-1">
                        {formik.errors.name}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-lg-6 col-md-6" key="version">
                  <div className="form-item">
                    <label className="custom-label mb-1 ff">Version</label>
                    <Input
                      name="version"
                      className="form-control form-control-lg"
                      value={formik.values.version}
                      placeholder="Version"
                      size="large"
                      status={
                        formik.touched.version && formik.errors.version
                          ? "error"
                          : ""
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        formik.setFieldValue("version", val);
                      }}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.version && formik.errors.version && (
                      <div className="text-danger mt-1">
                        {formik.errors.version}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h3 className="fs-4 fw-bold text-black mb-md-3 mb-2">
            Variable Costs{" "}
          </h3>
          <div className="card border-0 rounded-4 mb-md-4 mb-3 shadow-sm">
            <div className="card-body p-md-4 p-3">
              <div className="row g-md-4 g-3">
                {variableCosts.map((field) => (
                  <div className="col-lg-3 col-md-4 col-sm-6" key={field.name}>
                    <div className="form-item">
                      <label className="custom-label mb-1 ff d-flex align-items-center gap-1">
                        {field.label}{" "}
                        <Tooltip
                          title={`Click here to view ${field.label} information`}
                        >
                          <InfoCircleOutlined
                            onClick={() => {
                              const newData =
                                pricesheetVariablesInfo?.[field.name];
                              setVariableInformationData(newData);
                              setVariableInformationModal(true);
                            }}
                          />
                        </Tooltip>
                      </label>
                      <Input
                        name={field.name}
                        className="form-control form-control-lg"
                        value={
                          formik.values[
                            field.name as keyof typeof formik.values
                          ]
                        }
                        placeholder="0"
                        prefix={
                          <span className="text-white">{field.unit}</span>
                        }
                        size="large"
                        status={
                          formik.touched[
                            field.name as keyof typeof formik.values
                          ] &&
                          formik.errors[
                            field.name as keyof typeof formik.values
                          ]
                            ? "error"
                            : ""
                        }
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, "");
                          formik.setFieldValue(field.name, val);
                        }}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched[
                        field.name as keyof typeof formik.values
                      ] &&
                        formik.errors[
                          field.name as keyof typeof formik.values
                        ] && (
                          <div className="text-danger mt-1">
                            {
                              formik.errors[
                                field.name as keyof typeof formik.values
                              ]
                            }
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <h3 className="fs-4 fw-bold text-black mb-md-3 mb-2">
            Component / Product{" "}
          </h3>
          <div className="card border-0 rounded-4 mb-md-4 mb-3 shadow-sm">
            <div className="card-body p-md-4 p-3">
              <div className="row g-md-4 g-3">
                {componentProducts.map((field) => (
                  <div className="col-lg-4 col-md-6" key={field.name}>
                    <div className="form-item">
                      <label className="custom-label mb-1 ff d-flex align-items-center gap-1">
                        {field.label}{" "}
                        <Tooltip
                          title={`Click here to view ${field.label} information`}
                        >
                          <InfoCircleOutlined
                            onClick={() => {
                              const newData =
                                pricesheetVariablesInfo?.[field.name];
                              setVariableInformationData(newData);
                              setVariableInformationModal(true);
                            }}
                          />
                        </Tooltip>
                      </label>
                      <Input
                        name={field.name}
                        className="form-control form-control-lg"
                        value={
                          formik.values[
                            field.name as keyof typeof formik.values
                          ]
                        }
                        placeholder="Target / Hour"
                        prefix={<span className="text-white">Target/Hour</span>}
                        size="large"
                        status={
                          formik.touched[
                            field.name as keyof typeof formik.values
                          ] &&
                          formik.errors[
                            field.name as keyof typeof formik.values
                          ]
                            ? "error"
                            : ""
                        }
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, "");
                          formik.setFieldValue(field.name, val);
                        }}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched[
                        field.name as keyof typeof formik.values
                      ] &&
                        formik.errors[
                          field.name as keyof typeof formik.values
                        ] && (
                          <div className="text-danger mt-1">
                            {
                              formik.errors[
                                field.name as keyof typeof formik.values
                              ]
                            }
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <h3 className="fs-4 fw-bold text-black mb-md-3 mb-2">Notes</h3>
          <div className="card border-0 rounded-4 mb-md-4 mb-3 shadow-sm">
            <div className="card-body p-md-4 p-3">
              <div className="row g-md-4 g-3">
                <div className="col-12" key="description">
                  <div className="form-item">
                    <label className="custom-label mb-1 ff">Notes</label>
                    <TextArea
                      name="description"
                      className="form-control form-control-lg"
                      value={formik?.values?.description}
                      placeholder="Enter Notes"
                      size="large"
                      ref={targetDescriptionRef}
                      status={
                        formik?.touched?.description &&
                        formik?.errors?.description
                          ? "error"
                          : ""
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        formik.setFieldValue("description", val);
                      }}
                      onBlur={formik.handleBlur}
                    />
                    {formik?.touched?.description &&
                      formik?.errors?.description && (
                        <div className="text-danger mt-1">
                          {formik?.errors?.description}
                        </div>
                      )}
                  </div>
                </div>
                {getPageMode() === Modes.EDIT ? (
                  <>
                    <h3 className="fs-4 fw-bold text-black mb-0 d-flex justify-content-between align-items-center">
                      Recent Change Notes{" "}
                      {pricesheetHistory?.length > 0 ? (
                        <Link
                          target={
                            Capacitor.isNativePlatform() ? undefined : "_blank"
                          }
                          to={`/price-sheet/history/${id}`}
                          role="button"
                          className="text-primary fs-6 text-decoration-underline"
                        >
                          View All
                        </Link>
                      ) : (
                        ""
                      )}
                    </h3>
                    <div>
                      {pricesheetHistory?.length > 0 ? (
                        <Timeline
                          className="mt-1 pricesheet-timeline"
                          items={pricesheetHistory?.map((log: any) => ({
                            color: "black",
                            children: (
                              <div className="d-flex flex-column mb-3">
                                <p className="mb-1 fw-medium  text-949494">
                                  Changed by {log?.user_id?.name} on{" "}
                                  {momentFullTime(log.created_at)}
                                </p>
                                <p className="fw-medium mb-0">
                                  {log?.description}
                                </p>
                              </div>
                            ),
                          }))}
                        />
                      ) : (
                        <div className="d-flex align-items-center justify-content-center w-100 text-949494 fs-6">
                          You do not have any change notes yet.
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>
        </>
      )}
      <div className="row justify-content-center align-items-center">
        <div className="col-sm-auto col-6">
          <button
            type="button"
            className="btn btn-lg btn-outline-secondary px-4 w-100 text-uppercase"
            onClick={() => {
              navigate(-1);
            }}
          >
            Cancel
          </button>
        </div>
        <div className="col-sm-auto col-6">
          <button
            type="button"
            disabled={saving}
            className="btn btn-lg btn-primary px-5 w-100 text-uppercase"
            onClick={() => formik.handleSubmit()}
          >
            {saving ? (
              <BtnLoader />
            ) : getPageMode() === Modes.CREATE ? (
              "Create"
            ) : getPageMode() === Modes.DUPLICATE ? (
              "Duplicate"
            ) : (
              "Update"
            )}
          </button>
        </div>
      </div>
      {variableInformationModal && (
        <ViewPricesheetVariableModal
          visible={variableInformationModal}
          data={variableInformationData}
          onClose={() => {
            setVariableInformationModal(false);
            setVariableInformationData(null);
          }}
        />
      )}
      {/* <PricesheetNoteModal
        visible={openNoteModal}
        onClose={() => setOpenNoteModal(false)}
        handleSubmit={(values: any) => {}}
        loader={saving}
      /> */}
    </div>
  );
};

export default CreatePriceSheet;
